<template>
  <div class="friends-page">
    <!-- 左侧：好友列表 -->
    <div class="friend-list">
      <div class="title">
        <div class="icon">
          <FaIcon :icon="faAddressBook" :size="28" />
        </div>
        <span class="title-text">我的好友</span>
        <div class="title-actions">
          <button
            class="icon-action"
            type="button"
            title="查看收到 / 发出的好友申请"
            @click="requestVisible = true"
          >
            <FaIcon :icon="faUserClock" :size="24" />
            <span v-if="requestCount" class="badge">{{ requestCount }}</span>
          </button>
        </div>
      </div>

      <div class="search-field">
        <FaIcon :icon="faMagnifyingGlass" :size="13" class="search-icon" />
        <input
          v-model="keyword"
          class="search-input"
          type="text"
          placeholder="搜索好友"
        >
      </div>

      <div class="add-card">
        <div class="add-card-title">添加新的好友</div>
        <div class="add-card-row">
          <input
            v-model="searchKeyword"
            type="text"
            placeholder="输入昵称或邮箱"
            @keyup.enter="searchUser"
          >
          <button class="search-btn" type="button" title="搜索用户" @click="searchUser">
            <FaIcon :icon="faMagnifyingGlass" :size="13" />
          </button>
        </div>
        <div v-if="searchResults.length" class="search-results">
          <div v-for="u in searchResults" :key="u.id" class="search-item">
            <UserAvatar class="search-avatar" :url="u.avatarUrl" :name="u.nickname" />
            <span class="search-name">{{ u.nickname }}</span>
            <span v-if="u.relation === 'FRIEND'" class="search-tag">已是好友</span>
            <span v-else-if="u.relation === 'PENDING_SENT'" class="search-tag">待接受</span>
            <button
              v-else-if="u.relation === 'PENDING_RECEIVED'"
              class="mini-btn"
              type="button"
              title="去处理好友申请"
              @click="requestVisible = true"
            >
              <FaIcon :icon="faArrowRight" :size="11" />
            </button>
            <button
              v-else-if="u.relation !== 'SELF'"
              class="mini-btn primary"
              type="button"
              title="添加为好友"
              @click="addFriend(u)"
            >
              <FaIcon :icon="faUserPlus" :size="11" />
            </button>
          </div>
        </div>
        <div v-if="searchHint" class="search-empty">{{ searchHint }}</div>
        <div v-else-if="searched && !searchResults.length" class="search-empty">
          没有找到匹配的用户
        </div>
      </div>

      <div v-if="filtered.length" class="friend-items">
        <div
          v-for="friend in filtered"
          :key="friend.friendId"
          class="friend-item"
          :class="{ active: selectedFriendId === friend.friendId }"
          @click="selectFriend(friend)"
        >
          <UserAvatar
            class="avatar"
            :url="friend.avatarUrl"
            :name="displayName(friend)"
            :onair="isOnair(friend)"
          />
          <div class="info">
            <div class="name">{{ displayName(friend) }}</div>
            <FriendActivityLine :activity="activities[String(friend.friendId)]" />
            <div v-if="friend.remark" class="status">原名：{{ friend.nickname }}</div>
          </div>
          <button
            class="row-btn danger"
            type="button"
            title="删除好友"
            @click.stop="deleteFriend(friend)"
          >
            <FaIcon :icon="faTrashCan" :size="24" />
          </button>
        </div>
      </div>
      <div v-else class="empty">暂无好友，快添加一位吧</div>
    </div>

    <!-- 右侧：好友详情 -->
    <div class="schedule-wrap">
      <!-- 选中好友后，这一行标题由 FriendDetailPanel 里的「头像 + 名字 + 分享张数」接管 -->
      <div v-if="!selectedFriend" class="title">
        <div class="icon">
          <FaIcon :icon="faIdCard" :size="28" />
        </div>
        好友详情
      </div>

      <FriendDetailPanel
        v-if="selectedFriend"
        :friend="selectedFriend"
        :activity="activities[String(selectedFriend.friendId)]"
        class="detail-panel"
        @activity-changed="onActivityChanged"
      />
      <div v-else class="empty-schedule">
        请在左侧选择或添加一位好友
      </div>
    </div>

    <FriendRequestPanel
      :visible="requestVisible"
      @close="requestVisible = false"
      @changed="onRelationChanged"
    />
  </div>
</template>

<script>
import FriendDetailPanel from './friend/FriendDetailPanel.vue'
import FriendRequestPanel from './friend/FriendRequestPanel.vue'
import FriendActivityLine from './friend/FriendActivityLine.vue'
import FaIcon from '@/components/common/FaIcon.vue'
import UserAvatar from '@/components/common/UserAvatar.vue'
// 我的好友：通讯录（书 + 人），比原先的两人剪影更像「好友列表」，20px 下也最清晰
import { faAddressBook } from '@fortawesome/free-solid-svg-icons/faAddressBook'
// 好友申请：待处理的好友（时钟）
import { faUserClock } from '@fortawesome/free-solid-svg-icons/faUserClock'
// 注：「分享我的内容给好友」入口已迁到时间表页「我的工作区」标题右侧（TaskBlockPage.vue），
//    本页只保留好友申请入口。
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons/faMagnifyingGlass'
import { faUserPlus } from '@fortawesome/free-solid-svg-icons/faUserPlus'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight'
// 删除好友：垃圾桶（用户明确要求用垃圾桶，不要 faUserXmark）
import { faTrashCan } from '@fortawesome/free-solid-svg-icons/faTrashCan'
// 好友详情：名片（卡 + 人 + 信息行），比原来的宫格更贴近「详情」
import { faIdCard } from '@fortawesome/free-solid-svg-icons/faIdCard'
import {
  listFriends,
  listFriendActivities,
  searchUsers,
  sendFriendRequest,
  removeFriend,
  listFriendRequests
} from '@/api/friend.js'

export default {
  name: 'FriendsPage',
  components: { FriendDetailPanel, FriendRequestPanel, FriendActivityLine, FaIcon, UserAvatar },
  data() {
    return {
      friends: [],
      selectedFriend: null,
      selectedFriendId: null,
      keyword: '',
      searchKeyword: '',
      searchResults: [],
      searched: false,
      searchHint: '',
      requestCount: 0,
      requestVisible: false,
      // 好友动态（正在做什么 / 上一次做了什么），按 friendId 索引，挂在列表行昵称下方
      activities: {},
      // 动态刷新定时器：动态行带「此刻」语义，60s 拉一次保持新鲜
      activityTimer: null,
      faAddressBook,
      faUserClock,
      faMagnifyingGlass,
      faUserPlus,
      faArrowRight,
      faTrashCan,
      faIdCard
    }
  },
  computed: {
    filtered() {
      const k = this.keyword.trim().toLowerCase()
      if (!k) return this.friends
      return this.friends.filter(f =>
        this.displayName(f).toLowerCase().includes(k)
      )
    }
  },
  mounted() {
    this.loadFriends()
    this.loadRequestCount()
    this.loadActivities()
    this.activityTimer = setInterval(this.loadActivities, 60000)
  },
  beforeDestroy() {
    if (this.activityTimer) {
      clearInterval(this.activityTimer)
      this.activityTimer = null
    }
  },
  methods: {
    /** 拉取全部好友的当前动态，失败时静默保持上一次结果（动态行是附加信息，不该打扰主流程） */
    async loadActivities() {
      try {
        const res = await listFriendActivities()
        const list = (res.success && res.data) ? res.data : []
        const map = {}
        list.forEach(a => {
          if (a && a.friendId != null) map[String(a.friendId)] = a
        })
        this.activities = map
      } catch (e) {
        console.warn('[FriendsPage] 加载好友动态失败', e)
      }
    },
    /**
     * 好友工作表刷新时，响应已附带该好友最新实时状态（方案3）。
     * 就地更新左侧动态行，省去单独调用动态接口；60s 列表轮询保持不变作为兜底。
     * activity 为 null 表示该好友当前无动态 → 删除对应条目（动态行随之隐藏）。
     */
    onActivityChanged({ friendId, activity }) {
      if (friendId == null) return
      const key = String(friendId)
      if (activity) {
        this.$set(this.activities, key, activity)
      } else if (this.activities[key]) {
        this.$delete(this.activities, key)
      }
    },
    async loadFriends() {
      try {
        const res = await listFriends()
        this.friends = (res.success && res.data) ? res.data : []
        if (this.selectedFriendId) {
          const still = this.friends.find(
            f => String(f.friendId) === String(this.selectedFriendId)
          )
          if (!still) {
            this.selectedFriend = null
            this.selectedFriendId = null
          }
        }
      } catch (e) {
        console.error('[FriendsPage] 加载好友列表失败', e)
        this.friends = []
      }
    },
    async loadRequestCount() {
      try {
        const res = await listFriendRequests('INCOMING')
        this.requestCount = (res.success && res.data) ? res.data.length : 0
      } catch (e) {
        this.requestCount = 0
      }
    },
    async searchUser() {
      const kw = this.searchKeyword.trim()
      // 后端要求关键词至少 2 个字符（单字符 LIKE 会退化成全表扫描，也是枚举入口），
      // 这里提前拦一道，避免用户以为「搜了但没这个人」。
      if (kw.length < 2) {
        this.searched = false
        this.searchResults = []
        this.searchHint = kw ? '请至少输入 2 个字符' : ''
        return
      }
      this.searchHint = ''
      this.searched = true
      try {
        const res = await searchUsers(kw)
        this.searchResults = (res.success && res.data) ? res.data : []
      } catch (e) {
        console.error('[FriendsPage] 搜索用户失败', e)
        this.searchResults = []
      }
    },
    async addFriend(user) {
      try {
        const res = await sendFriendRequest(user.id, {})
        if (!res.success || res.code !== 200) {
          alert(res.message || '发送申请失败')
          return
        }
        alert('好友申请已发送')
        await this.searchUser()
      } catch (e) {
        alert('网络异常，请稍后重试')
      }
    },
    async deleteFriend(friend) {
      if (!window.confirm('确定删除该好友吗？')) return
      try {
        const res = await removeFriend(friend.friendId)
        if (!res.success || res.code !== 200) {
          alert(res.message || '删除失败')
          return
        }
        await this.loadFriends()
      } catch (e) {
        alert('网络异常，请稍后重试')
      }
    },
    displayName(friend) {
      return friend.remark || friend.nickname || '好友'
    },
    /** 该好友是否「正在做」：决定头像绿色光环与昵称下方动态行的绿色态 */
    isOnair(friend) {
      const activity = this.activities[String(friend.friendId)]
      return !!(activity && activity.status === 'RUNNING')
    },
    selectFriend(friend) {
      this.selectedFriend = friend
      this.selectedFriendId = friend.friendId
    },
    onRelationChanged() {
      this.loadFriends()
      this.loadRequestCount()
      this.loadActivities()
    }
  }
}
</script>

<style scoped>
.friends-page {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Microsoft YaHei", sans-serif;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  gap: 0.938rem;
  overflow: hidden;
}

.friends-page * {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Microsoft YaHei", sans-serif;
}

.friend-list,
.schedule-wrap {
  padding: 15px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.friend-list {
  flex: 2;
  background: #edf4d8;
  border: 4px solid #57795c;
  border-radius: 18px;
  box-shadow: 0 6px 0 #426246, 0 12px 20px rgba(0, 0, 0, 0.15);
}

.schedule-wrap {
  flex: 8;
  background: #f5f1d5;
  border: 4px solid #698b52;
  border-radius: 0.5rem;
  box-shadow: 3px 3px 0 #587342;
}

.detail-panel {
  flex: 1;
  min-height: 0;
}

.title {
  font-size: 22px;
  font-weight: bold;
  color: #263b29;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.title-text {
  flex: none;
}

/* 好友申请：图标按钮，靠右贴在「我的好友」这一行（分享入口已迁到时间表页） */
.title-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* 好友申请：图标 24px + 按钮框 32px（用户指定图标 24px，按钮框保留 32px）。
   注意这几个图标的 viewBox 多为 0 0 576 512，字形在框内还有留白，
   标称 24px 实际可见高度约 21px；按钮框内四周各留 4px。 */
.icon-action {
  position: relative;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #987a2c;
  background: #f1dc7b;
  color: #263b29;
  border-radius: 11px;
  padding: 0;
  cursor: pointer;
  transition: background 0.15s ease;
}

.icon-action:hover {
  background: #e8d06a;
}

.icon {
  width: 32px;
  height: 32px;
  background: #f1dc7b;
  border: 3px solid #80672c;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #263b29;
}

/* 申请数角标：贴在图标右上角，描边用面板底色做出「挖空」效果（随按钮一起放大） */
.badge {
  position: absolute;
  top: -7px;
  right: -7px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #c45c5c;
  color: #fff;
  border: 2px solid #edf4d8;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

/* 好友搜索框：放大镜图标内嵌在输入框里 */
.search-field {
  position: relative;
  flex-shrink: 0;
  margin-bottom: 14px;
}

.search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #8a9a78;
}

.search-input {
  width: 100%;
  border: none;
  background: #f7f7df;
  border-radius: 12px;
  padding: 12px 14px 12px 36px;
  font-size: 14px;
  color: #263b29;
  outline: none;
  transition: box-shadow 0.15s ease;
}

.search-input:focus {
  box-shadow: 0 0 0 3px rgba(145, 166, 123, 0.35);
}

.search-input::placeholder {
  color: #8a9a78;
}

.add-card {
  background: #edf2d5;
  border-radius: 16px;
  border: 2px dashed #91a67b;
  box-shadow: 0 6px 16px -8px rgba(87, 121, 92, 0.25);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
  flex-shrink: 0;
}

.add-card-title {
  font-size: 14px;
  color: #57795c;
  font-weight: 700;
}

.add-card-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.add-card-row input {
  flex: 1;
  min-width: 0;
  border: none;
  background: #f7f7df;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
  color: #263b29;
  outline: none;
}

.add-card-row input:focus {
  box-shadow: 0 0 0 3px rgba(145, 166, 123, 0.35);
}

.add-card-row input::placeholder {
  color: #8a9a78;
}

.search-btn {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f1dc7b;
  color: #263b29;
  border: 2px solid #987a2c;
  border-radius: 12px;
  cursor: pointer;
}

.search-btn:hover {
  background: #e8d06a;
}

.search-results {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 160px;
  overflow-y: auto;
}

.search-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f7f7df;
  border-radius: 10px;
  padding: 6px 8px;
}

.search-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #a8c8e0;
  border: 2px solid #57795c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.search-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #263b29;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-tag {
  font-size: 11px;
  color: #8a9a78;
  flex-shrink: 0;
}

.mini-btn {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: #e6eecb;
  color: #57795c;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.mini-btn:hover {
  background: #d5e2b4;
  color: #35512d;
}

.mini-btn.primary {
  background: #f1dc7b;
  color: #263b29;
}

.mini-btn.primary:hover {
  background: #e8d06a;
}

.friend-items {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 4px;
}

.friend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #edf2d5;
  border-radius: 14px;
  border: 2px solid #91a67b;
  box-shadow: 0 6px 16px -8px rgba(87, 121, 92, 0.22);
  cursor: pointer;
  /* 不参与 transform：点击/悬停时整行不再向上弹起，只用底色 + 描边 + 光环表达选中 */
  transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

/* 选中态：底色 + 描边 + 主色光环，一眼看出当前看的是哪位好友的时间表 */
.friend-item.active {
  background: #e8f0d0;
  border-color: #57795c;
  box-shadow: 0 0 0 3px rgba(87, 121, 92, 0.16);
}

.avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  color: #fff;
  flex-shrink: 0;
  border: 2px solid #57795c;
  background: #b8d49b;
}

.info {
  flex: 1;
  min-width: 0;
}

.name {
  font-size: 15px;
  color: #263b29;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status {
  font-size: 12px;
  color: #57795c;
  margin-top: 3px;
  font-weight: 600;
}

/* 行内图标按钮（删除好友）：默认低调，悬停才显色，避免抢走好友名的注意力。
   垃圾桶图标与上方动作图标统一为 24px；按钮框保留 32px。 */
.row-btn {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #a3b294;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.row-btn:hover {
  background: #f7f7df;
  color: #57795c;
}

.row-btn.danger:hover {
  background: #fde8e8;
  color: #c45c5c;
}

.empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #57795c;
  font-size: 14px;
  font-weight: 600;
}

.empty-schedule {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #57795c;
  font-size: 18px;
  font-weight: 700;
}

@media (max-width: 900px) {
  .friends-page {
    flex-direction: column;
    overflow: auto;
  }

  .friend-list,
  .schedule-wrap {
    flex: none;
    height: auto;
    overflow: visible;
  }

  .friend-items {
    overflow: visible;
  }
}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   只改颜色 / 描边 / 圆角 / 阴影，尺寸与间距全部沿用上面的原值。 */
/* 左右两个大板块：米黄厚涂 + 硬阴影 → 白底 + 块级重描边 + 柔阴影 */
.friend-list,
.schedule-wrap{
  background:var(--lp-surface,#fff);
  border:3px solid var(--lp-border-strong,#a5cdb3);
  border-radius:1rem;
  box-shadow:var(--lp-shadow,0 2px 10px rgba(52,102,68,.06));
}
/* 标题 / 图标徽章 / 顶部图标按钮 */
/* 图标与文字间距：原来 .icon 是个 32×32 的方框，20px 图标居中后左右各留 6px 空白，
   再叠加 gap:10px → 视觉距离 16px，用户反馈「很远」。
   第一轮：方框收缩到贴合图标（宽高 auto）+ gap 收到 8px；用户仍觉得远 → 再收到 5px。
   5px 又被反馈「太挤」→ 微调回 8px，取「紧凑但不贴住」的折中值。
   （这条动了尺寸，是用户明确要求调整间距，属于对「只改颜色」约定的合理违反） */
.title{
  color:var(--lp-text,#26332b);
  gap:8px;
}
.icon{
  background:transparent;
  border:none;
  width:auto;
  height:auto;
  color:var(--lp-primary-dark,#2f7a4a);
}
.icon-action{
  background:transparent;
  border:none;
  border-radius:0.625rem;
  color:var(--lp-text-3,#94a59b);
}
.icon-action:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
.badge{
  background:#d9534f;
  border-color:var(--lp-surface,#fff);
}
/* 好友搜索框 */
.search-icon{
  color:var(--lp-text-3,#94a59b);
}
.search-input,
.add-card-row input{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  color:var(--lp-text,#26332b);
}
.search-input:focus,
.add-card-row input:focus{
  border-color:var(--lp-primary,#4aa46a);
  box-shadow:none;
}
.search-input::placeholder,
.add-card-row input::placeholder{
  color:var(--lp-text-3,#94a59b);
}
/* 添加好友卡片 */
.add-card{
  background:var(--lp-surface-2,#f7fbf8);
  border:2px dashed var(--lp-border,#e3efe6);
  border-radius:0.875rem;
  box-shadow:none;
}
.add-card-title{
  color:var(--lp-primary-dark,#2f7a4a);
}
.search-btn{
  background:var(--lp-primary,#4aa46a);
  border:2px solid var(--lp-primary,#4aa46a);
  border-radius:0.5rem;
  color:#fff;
}
.search-btn:hover{
  background:var(--lp-primary-dark,#2f7a4a);
}
/* 搜索结果行 */
.search-item{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.75rem;
}
.search-name{
  color:var(--lp-text,#26332b);
}
.search-tag{
  color:var(--lp-text-3,#94a59b);
}
.mini-btn{
  background:var(--lp-primary-soft,#e9f5ec);
  border-radius:0.5rem;
  color:var(--lp-primary-dark,#2f7a4a);
}
.mini-btn:hover{
  background:var(--lp-primary-soft2,#d5ebdb);
  color:var(--lp-primary-dark,#2f7a4a);
}
.mini-btn.primary{
  background:var(--lp-primary,#4aa46a);
  color:#fff;
}
.mini-btn.primary:hover{
  background:var(--lp-primary-dark,#2f7a4a);
}
/* 好友列表行 */
.friend-item{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.75rem;
  box-shadow:var(--lp-shadow,0 2px 10px rgba(52,102,68,.06));
}
.friend-item:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-primary-soft2,#d5ebdb);
  box-shadow:var(--lp-shadow,0 2px 10px rgba(52,102,68,.06));
}
/* 选中态：实心主色描边 + 外扩光环（不用 transform，避免整行弹起跳动） */
.friend-item.active{
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-primary,#4aa46a);
  box-shadow:0 0 0 3px rgba(74,164,106,.14),var(--lp-shadow,0 2px 10px rgba(52,102,68,.06));
}
/* 头像：统一成主色实心 + 白字 */
.avatar,
.search-avatar{
  background:var(--lp-primary,#4aa46a);
  border-color:var(--lp-primary-dark,#2f7a4a);
}
.name{
  color:var(--lp-text,#26332b);
}
.status{
  color:var(--lp-text-2,#5f7268);
}
/* 行内图标按钮 */
.row-btn{
  color:var(--lp-text-3,#94a59b);
}
.row-btn:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
.row-btn.danger:hover{
  background:#fdecec;
  color:#d9534f;
}
/* 空态 */
.empty,
.empty-schedule{
  color:var(--lp-text-3,#94a59b);
}
</style>
